import React from "react";
import { makeStyles } from "@mui/styles";
import Toolbar from "@mui/material/Toolbar";
import { negociosSeguros } from "~constants/negocios";
import { condicionesFiscalesOptions } from "~constants/condicionesFiscales";
import Typography from "@mui/material/Typography";
import { DetalleCotizacionView } from "../types";

const useStyles = makeStyles({
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    flexDirection: "row",
    alignItems: "flex-start",
    // El Toolbar de MUI reserva un alto minimo propio que aca sobra: la altura
    // la tiene que dar el contenido.
    minHeight: "auto",
  },
  // Celda con dos datos apilados: el de abajo arranca en la misma columna que el
  // de arriba, en vez de quedar desplazado como quedaria en una fila aparte.
  stackedCell: {
    display: "flex",
    flexDirection: "column",
    rowGap: 8,
  },
});

interface MainDataProps {
  detalleCotizacion: DetalleCotizacionView;
}

const MainData: React.FC<MainDataProps> = ({ detalleCotizacion }) => {
  const classes = useStyles();

  const negocioLabelMap = negociosSeguros as Record<string, string>;
  const condicionFiscalLabelMap = condicionesFiscalesOptions as Record<
    string,
    string
  >;

  // El mapa de etiquetas indexado con cadena vacia devuelve "Todas", que es la
  // opcion del filtro del listado y no una condicion fiscal. Por eso se descarta
  // el valor vacio antes de buscar la etiqueta, y se cae al valor crudo si todavia
  // no tiene una.
  const condicionFiscal = detalleCotizacion.condicionFiscal
    ? (condicionFiscalLabelMap[detalleCotizacion.condicionFiscal] ??
      detalleCotizacion.condicionFiscal)
    : "-";

  return (
    <Toolbar className={classes.toolbar}>
      <MainDataItem
        caption={"Negocio"}
        value={negocioLabelMap[detalleCotizacion.negocio]}
      />
      <MainDataItem caption={"Origen"} value={detalleCotizacion.origen} />
      <MainDataItem
        caption={"Cotizó"}
        value={
          detalleCotizacion.nombrePersona !== null
            ? detalleCotizacion.nombrePersona
            : "-"
        }
      />
      <MainDataItem
        caption={"E-Mail"}
        value={detalleCotizacion.email !== null ? detalleCotizacion.email : "-"}
      />
      <MainDataItem
        caption={"Teléfono"}
        value={
          detalleCotizacion.telefono !== null ? detalleCotizacion.telefono : "-"
        }
      />
      <MainDataItem
        caption={"Vehículo"}
        value={`(${detalleCotizacion.anio}) ${detalleCotizacion.marca} ${detalleCotizacion.modelo}`}
      />
      <MainDataItem
        caption={"Localidad"}
        value={`(${detalleCotizacion.codigoPostal}) ${
          detalleCotizacion.localidad ?? "-"
        }, ${detalleCotizacion.provincia ?? "-"}`}
      />

      <div className={classes.stackedCell}>
        <MainDataItem
          caption={"Forma de Pago"}
          value={detalleCotizacion.formaPago}
        />

        <MainDataItem caption={"Condición Fiscal"} value={condicionFiscal} />
      </div>

      <div className={classes.stackedCell}>
        <MainDataItem
          caption={"GNC"}
          value={detalleCotizacion.tieneGNC ? "Sí" : "No"}
        />

        <MainDataItem caption={"Uso"} value={detalleCotizacion.uso ?? "-"} />
      </div>
    </Toolbar>
  );
};

export default MainData;

const useStylesItem = makeStyles((theme) => ({
  mainDataItemContainer: {
    display: "flex",
    flexDirection: "column",
    paddingRight: 30,
  },
  mainDataTitle: {
    fontFamily: "Nunito",
    fontSize: "0.7em",
    fontWeight: 500,
    fontStyle: "normal",
    fontStretch: "normal",
    lineHeight: 1.3,
    letterSpacing: "normal",
    textAlign: "left",
    color: theme.colours.grey2,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  mainDataValue: {
    //width: "102px",
    // Altura minima y no fija: un valor largo que ocupa varias lineas agranda su
    // celda y el encabezado, en vez de derramarse sobre el contenido de abajo.
    minHeight: "19px",
    fontFamily: "Nunito",
    fontSize: "0.8em",
    fontWeight: 600,
    fontStyle: "normal",
    fontStretch: "normal",
    lineHeight: 1.36,
    letterSpacing: "normal",
    textAlign: "left",
    color: theme.colours.grey5,
  },
}));

interface MainDataItemProps {
  caption: string;
  value: React.ReactNode;
}

const MainDataItem: React.FC<MainDataItemProps> = ({ caption, value }) => {
  const classes = useStylesItem();

  return (
    <div className={classes.mainDataItemContainer}>
      <Typography className={classes.mainDataTitle}>{caption}</Typography>
      <Typography className={classes.mainDataValue}>{value}</Typography>
    </div>
  );
};
